"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GameScene = void 0;
const scene_1 = require("@babylonjs/core/scene");
const math_vector_1 = require("@babylonjs/core/Maths/math.vector");
const math_color_1 = require("@babylonjs/core/Maths/math.color");
const directionalLight_1 = require("@babylonjs/core/Lights/directionalLight");
const hemisphericLight_1 = require("@babylonjs/core/Lights/hemisphericLight");
const Screens_1 = __importDefault(require("./Screens"));
const PlayerInput_1 = require("../Controllers/PlayerInput");
const UserInterface_1 = require("../Controllers/UserInterface");
const Player_1 = require("../Entities/Player");
const Entity_1 = require("../Entities/Entity");
const Item_1 = require("../Entities/Item");
const navMeshHelper_1 = require("../Utils/navMeshHelper");
const loadNavMeshFromString_1 = __importDefault(require("../Utils/loadNavMeshFromString"));
const types_1 = require("../../shared/types");
const standardMaterial_1 = require("@babylonjs/core/Materials/standardMaterial");
const texture_1 = require("@babylonjs/core/Materials/Textures/texture");
const VatController_1 = require("../Controllers/VatController");
const meshBuilder_1 = require("@babylonjs/core/Meshes/meshBuilder");
const cubeTexture_1 = require("@babylonjs/core/Materials/Textures/cubeTexture");
const shadowGenerator_1 = require("@babylonjs/core/Lights/Shadows/shadowGenerator");
const PlayerCamera_1 = require("../Entities/Player/PlayerCamera");
const SoundController_1 = require("../Controllers/SoundController");
class GameScene {
    constructor() {
        this._loadedAssets = [];
        this._entities = new Map();
        this.toSpawnOthers = new Map();
        this.playerIsSpawned = false;
    }
    createScene(game) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            // app
            this._game = game;
            // create scene
            let scene = new scene_1.Scene(this._game.engine);
            // set scene
            this._scene = scene;
            // if no user logged in, force a auto login
            // to be remove later or
            if (!this._game.isLoggedIn()) {
                yield this._game.forceLogin();
            }
            // check if user token is valid
            let user = yield this._game.isValidLogin();
            if (!user) {
                // if token not valid, send back to login screen
                this._game.setScene(Screens_1.default.LOGIN);
            }
            // performance
            scene.performancePriority = scene_1.ScenePerformancePriority.Intermediate;
            // get location details
            let location = this._game.currentLocation;
            // add background  color
            let color = location.skyColor;
            scene.clearColor = new math_color_1.Color4(color[0], color[1], color[2], color[3]);
            if (this._game.config.SHADOW_ON === true) {
                // shadow light
                // https://forum.babylonjs.com/t/shadow-doesnt-work-if-another-light-is-created-before-shadow-casting-light/39852/4
                var shadowLight = new directionalLight_1.DirectionalLight("DirectionalLight", new math_vector_1.Vector3(-0.5, -5, -0.5), scene);
                //shadowLight.position = new Vector3(1, 10, 1);
                shadowLight.intensity = 1;
                shadowLight.autoCalcShadowZBounds = true;
                // performant shadow
                // some settings I have found looks good
                // https://forum.babylonjs.com/t/shadows-setup-for-large-usually-static-world/39636/11?u=oriongu
                const shadowGenerator = new shadowGenerator_1.ShadowGenerator(2048, shadowLight);
                shadowGenerator.enableSoftTransparentShadow = true;
                shadowGenerator.transparencyShadow = true;
                shadowGenerator.filter = shadowGenerator_1.ShadowGenerator.FILTER_BLURCLOSEEXPONENTIALSHADOWMAP;
                this._shadow = shadowGenerator;
                // if you move anything you can force it to render by calling
                //this._shadow.getShadowMap().resetRefreshCounter();
            }
            // add sky
            if (location.sun) {
                // skybox
                const skybox = meshBuilder_1.MeshBuilder.CreateBox("skyBox", { size: 1000.0 }, scene);
                const skyboxMaterial = new standardMaterial_1.StandardMaterial("skyBox", scene);
                skyboxMaterial.backFaceCulling = false;
                skyboxMaterial.disableLighting = true;
                skybox.material = skyboxMaterial;
                skybox.infiniteDistance = true;
                skyboxMaterial.reflectionTexture = new cubeTexture_1.CubeTexture("textures/skybox", scene);
                skyboxMaterial.reflectionTexture.coordinatesMode = texture_1.Texture.SKYBOX_MODE;
                // sunlight
                var sunLight = new hemisphericLight_1.HemisphericLight("light1", new math_vector_1.Vector3(0, 1, 0), scene);
                sunLight.intensity = location.sunIntensity;
                sunLight.groundColor = new math_color_1.Color3(0.13, 0.13, 0.13);
                sunLight.specular = math_color_1.Color3.Black();
            }
            // add fog
            if (location.fog === true) {
                scene.fogMode = scene_1.Scene.FOGMODE_LINEAR;
                scene.fogStart = 70.0;
                scene.fogEnd = 200.0;
                scene.fogColor = new math_color_1.Color3(0.9, 0.9, 0.85);
            }
            // load navmesh
            this._navMesh = yield this.loadNavMesh(location.key);
            this._navMeshDebug = (0, navMeshHelper_1.createConvexRegionHelper)(this._navMesh, this._scene); // function to show the navmesh as a mesh in-game
            this._navMeshDebug.isVisible = false;
            // initialize assets controller & load level
            this._game.initializeAssetController(this._shadow);
            yield this._game._assetsCtrl.loadLevel(location.key);
            this._game.engine.displayLoadingUI();
            // preload any skeletons and animation
            let spawns = (_a = location.dynamic.spawns) !== null && _a !== void 0 ? _a : [];
            this._game._vatController = new VatController_1.VatController(this._game, spawns);
            yield this._game._vatController.initialize();
            console.log("[VAT] fully loaded", this._game._vatController._entityData);
            // init network
            this._initNetwork();
        });
    }
    loadNavMesh(key) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield (0, loadNavMeshFromString_1.default)(key);
        });
    }
    _initNetwork() {
        return __awaiter(this, void 0, void 0, function* () {
            // join global chat room if not already connected
            if (this._game.currentChat) {
                this._game.currentChat.leave();
            }
            this._game.currentChat = yield this._game.client.joinChatRoom({ name: this._game._currentCharacter.name });
            // join the game room and use chat room session ID
            this.room = yield this._game.client.joinOrCreateRoom(this._game._currentCharacter.location, this._game._currentUser.token, this._game._currentCharacter.id);
            this._game.currentRoom = this.room;
            if (this.room) {
                // set room onError evenmt
                this.room.onError((code, message) => {
                    this._game.setScene(Screens_1.default.LOGIN);
                });
                // set room onLeave event
                this.room.onLeave((code) => {
                    if (code === 1006) {
                        this._game.setScene(Screens_1.default.LOGIN);
                    }
                });
                // initialize game
                yield this._initGame();
            }
            else {
            }
        });
    }
    _initGame() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            // so we can access it later
            this._game.gamescene = this;
            // setup interface
            this._ui = new UserInterface_1.UserInterface(this._game, this._entities, this._currentPlayer);
            // setup input Controller
            this._input = new PlayerInput_1.PlayerInput(this);
            this._camera = new PlayerCamera_1.PlayerCamera(this);
            this._sound = new SoundController_1.SoundController(this);
            // start music controller
            //this._sound.play(this._game.currentLocation.music, true);
            ////////////////////////////////////////////////////
            //  when a entity joins the room event
            this.room.state.entities.onAdd((entity, sessionId) => {
                if (entity.type === "player" && entity.sessionId === this.room.sessionId) {
                    this.toSpawnPlayer = entity;
                }
                else {
                    this.toSpawnOthers.set(entity.sessionId, entity);
                }
            });
            // when an entity is removed
            this.room.state.entities.onRemove((entity, sessionId) => {
                var _a;
                if (this._entities.has(sessionId)) {
                    (_a = this._entities.get(sessionId)) === null || _a === void 0 ? void 0 : _a.remove();
                    this._entities.delete(sessionId);
                }
            });
            ////////////////////////////////////////////////////
            // main game loop
            const lastUpdates = {
                SERVER: {
                    RATE: (_a = this._game.config.updateRate) !== null && _a !== void 0 ? _a : 100,
                    TIME: Date.now(),
                },
                SLOW: {
                    RATE: 1000,
                    TIME: Date.now(),
                },
                PING: {
                    RATE: 2000,
                    TIME: Date.now(),
                },
                UI_SERVER: {
                    RATE: 100,
                    TIME: Date.now(),
                },
                UI_SLOW: {
                    RATE: 1000,
                    TIME: Date.now(),
                },
            };
            // start game loop
            this._scene.registerBeforeRender(() => {
                // get current delta
                let delta = this._game.engine.getFps();
                // process vat animations
                this._game._vatController.process(delta);
                // iterate through each items
                const currentTime = Date.now();
                if (this.playerIsSpawned) {
                    this._entities.forEach((entity, sessionId) => {
                        // main entity update
                        entity.update(delta);
                        // server player gameloop
                        if (currentTime - lastUpdates.SERVER.TIME >= lastUpdates.SERVER.RATE) {
                            entity.updateServerRate(lastUpdates.SERVER.RATE);
                        }
                        // slow game loop
                        if (currentTime - lastUpdates.SLOW.TIME >= lastUpdates.SLOW.RATE) {
                            entity.updateSlowRate(lastUpdates.SLOW.RATE);
                            entity.lod(this._currentPlayer);
                        }
                    });
                }
                // reset timers
                if (currentTime - lastUpdates.SERVER.TIME >= lastUpdates.SERVER.RATE) {
                    lastUpdates.SERVER.TIME = currentTime;
                }
                if (currentTime - lastUpdates.SLOW.TIME >= lastUpdates.SLOW.RATE) {
                    lastUpdates.SLOW.TIME = currentTime;
                }
                // game update loop
                if (currentTime - lastUpdates.PING.TIME >= lastUpdates.PING.RATE) {
                    this._game.sendMessage(types_1.ServerMsg.PING); // send ping to server
                    lastUpdates.PING.TIME = currentTime;
                }
                // ui update loop
                if (currentTime - lastUpdates.UI_SERVER.TIME >= lastUpdates.UI_SERVER.RATE) {
                    this._ui.update();
                    lastUpdates.UI_SERVER.TIME = currentTime;
                    // spawn entities
                    if (this.toSpawnOthers.size > 0) {
                        this.spawn();
                    }
                }
                // spawn player
                if (!this.playerIsSpawned && this.toSpawnPlayer) {
                    // create player entity
                    let _player = new Player_1.Player(this.toSpawnPlayer.sessionId, this._scene, this, this.toSpawnPlayer);
                    // set currentPlayer
                    this._currentPlayer = _player;
                    // add player specific ui
                    this._ui.setCurrentPlayer(_player);
                    // add to entities
                    this._entities.set(this.toSpawnPlayer.sessionId, _player);
                    // hide
                    this._game.engine.hideLoadingUI();
                    // only do it once
                    this.playerIsSpawned = true;
                }
            });
            this._scene.registerAfterRender(() => {
                const currentTime = Date.now();
                // ui slow update loop
                if (currentTime - lastUpdates.UI_SLOW.TIME >= lastUpdates.UI_SLOW.RATE) {
                    this._ui.slow_update();
                    lastUpdates.UI_SLOW.TIME = currentTime;
                }
            });
        });
    }
    engineUpdate() { }
    spawn() {
        return __awaiter(this, void 0, void 0, function* () {
            let amountToSpawn = 10;
            let i = 0;
            for (const value of this.toSpawnOthers) {
                let entity = value[1];
                i++;
                // if player
                if (entity.type === "player" || entity.type === "entity") {
                    this._entities.set(entity.sessionId, new Entity_1.Entity(entity.sessionId, this._scene, this, entity));
                }
                // if item
                if (entity.type === "item") {
                    this._entities.set(entity.sessionId, new Item_1.Item(entity.sessionId, this._scene, entity, this.room, this._ui, this._game));
                }
                // remove
                this.toSpawnOthers.delete(entity.sessionId);
                // only spawn 1 every frame
                if (i > amountToSpawn) {
                    break;
                }
            }
        });
    }
    // triggered on resize event
    resize() {
        if (this._ui) {
            this._ui.resize();
        }
    }
}
exports.GameScene = GameScene;
